import React from 'react';
import PropTypes from 'prop-types';
import withStyles from '@material-ui/core/styles/withStyles';

import Title from '../../Title';
import { compose } from 'redux';
import { connect } from 'react-redux';

import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import LandValueIcon from '@material-ui/icons/LocalOffer';
import PerHectareIcon from '@material-ui/icons/CropFree';

import { Typography, Tooltip, Menu, MenuItem } from '@material-ui/core';
import { Skeleton } from '@material-ui/lab';

class LandUse extends React.Component {
    static propTypes = {
        classes: PropTypes.object.isRequired,
        // Redux State
        landUseValues: PropTypes.object
    };

    constructor (props) {
        super(props);

        this.state = {
            anchorEl: null,
            selectedLandUse: null
        };

        this.handleMenuOpen = this.handleMenuOpen.bind(this);
        this.handleMenuItemClick = this.handleMenuItemClick.bind(this);
        this.handleMenuClose = this.handleMenuClose.bind(this);
    }

    componentDidUpdate (prevProps) {
        if ((prevProps.landUseValues != this.props.landUseValues) && this.props.landUseValues) {
            this.setState({
                selectedLandUse: this.props.landUseValues.landUse
            });
        }
    }

    handleMenuOpen (event) {
        this.setState({ anchorEl: event.currentTarget });
    }

    handleMenuItemClick (landUse) {
        return function (e) {
            this.setState({
                anchorEl: null,
                selectedLandUse: landUse
            });
        }.bind(this);
    }

    handleMenuClose () {
        this.setState({ anchorEl: null });
    }

    render () {
        const { classes, landUseValues } = this.props;
        const { anchorEl, selectedLandUse } = this.state;

        const isLoading = !Boolean(landUseValues);

        const landUse = !isLoading && (selectedLandUse || landUseValues.landUse || 'unknown');
        const land = !isLoading && landUseValues.landValues[landUse];

        return (
            <>
                <Title
                    title="BENCHMARK LAND VALUE"
                    skeleton={isLoading}
                >
                </Title>
                {
                    isLoading ?
                        <Skeleton animation="wave" height={154} variant="rect" /> :
                        <div className={classes.container}>
                            <div className={classes.landUseSelector} onClick={this.handleMenuOpen}>
                                <Typography className={classes.withTooltip} variant="body1" color="textPrimary">
                                    Land Use
                                    <Tooltip arrow title={'The current land use is determined by the land use layer which may not always be accurate, change to a different land use if you have other knowledge of the location'} placement={'right'}>
                                        <InfoOutlinedIcon />
                                    </Tooltip>
                                </Typography>
                                <Typography variant="h5" color="textPrimary">
                                    { landUse.charAt(0).toUpperCase() + landUse.slice(1) }
                                </Typography>
                                <div className={classes.dropwdown}>
                                    <ArrowDropDownIcon />
                                </div>
                            </div>
                            <Menu
                                classes={{ paper: classes.menuList }}
                                open={Boolean(anchorEl)}
                                anchorEl={anchorEl}
                                onClose={this.handleMenuClose}
                                disableAutoFocusItem
                                getContentAnchorEl={null}
                                anchorOrigin={{
                                    vertical: 'bottom',
                                    horizontal: 'center'
                                }}
                                transformOrigin={{
                                    vertical: 'top',
                                    horizontal: 'center'
                                }}
                                PaperProps={{
                                    style: { width: anchorEl ? anchorEl.clientWidth + 2: 0 }
                                }}
                            >
                                {
                                    Object.keys(landUseValues.landValues)
                                        .filter(key => (landUse !== key))
                                        .map(key =>
                                            <MenuItem
                                                key={key}
                                                onClick={this.handleMenuItemClick(key)}
                                            >
                                                { key.charAt(0).toUpperCase() + key.slice(1) } { (landUseValues.landUse && landUseValues.landUse === key) && '(Current)'}
                                            </MenuItem>
                                        )
                                }
                            </Menu>
                            <div className={classes.horizontalContainer}>
                                <div className={classes.box}>
                                    <Typography variant="body1" color="textPrimary">
                                        Estimated Land Value
                                    </Typography>
                                    <Typography className={classes.stat} color="textPrimary">
                                        <LandValueIcon />
                                        { (land && land.landValue) ? '£' + land.landValue.toLocaleString(undefined, { maximumFractionDigits:0 }) : 'N/A' }
                                    </Typography>
                                </div>
                                <div className={classes.box}>
                                    <Typography className={classes.withTooltip} variant="body1" color="textPrimary">
                                        Per Hectare
                                        <Tooltip arrow title={`Pound per Hectare for ${landUseValues.authorityName} local authority district for ${landUse} land use based on Valuation Office Agency`} placement={'right'}>
                                            <InfoOutlinedIcon />
                                        </Tooltip>
                                    </Typography>
                                    <Typography className={classes.stat} color="textPrimary">
                                        <PerHectareIcon />
                                        { (land && land.ppha) ? '£' + land.ppha.toLocaleString(undefined, { maximumFractionDigits:0 }) : 'N/A' }
                                    </Typography>
                                </div>
                            </div>
                        </div>
                }
            </>
        );
    }
}

const style = theme => ({
    container: {
        display: 'flex',
        flexDirection: 'column',
        border: `1px solid ${theme.palette.background.border}`,
        borderRadius: '5px',
        '&> *': {
            '&:not(:last-child)': {
                borderBottom: `1px solid ${theme.palette.background.border}`
            }
        }
    },
    horizontalContainer: {
        display: 'flex',
        flexDirection: 'row',
        '&> *': {
            flex: 1,
            '&:not(:last-child)': {
                borderRight: `1px solid ${theme.palette.background.border}`
            }
        }
    },
    landUseSelector: {
        padding: '10px 15px',
        position: 'relative',
        cursor: 'pointer'
    },
    box: {
        padding: '10px 15px'
    },
    stat: {
        ...theme.typography.stat2,
        display: 'flex',
        alignItems: 'center',
        '&> svg': {
            marginRight: 5
        }
    },
    withTooltip: {
        display: 'flex',
        alignItems: 'center',
        '&> svg': {
            fontSize: '1em',
            marginLeft: '6px'
        }
    },
    menuList: {
        backgroundColor: theme.palette.background.main,
        borderBottom: `1px solid ${theme.palette.background.border}`,
        borderLeft: `1px solid ${theme.palette.background.border}`,
        borderRight: `1px solid ${theme.palette.background.border}`,
        borderRadius: '0px 0px 5px 5px',
        boxSizing: 'border-box',
        '&> ul': {
            padding: 0
        },
        ...theme.components.scrollable
    },
    dropwdown: {
        position: 'absolute',
        right: 15,
        top: 0,
        bottom: 0,
        display: 'flex',
        alignItems: 'center',
        color: theme.palette.text.primary
    }
});

const mapStateToProps = (state) => {
    const currentPolygon = state.parcelPanel.currentPolygonId && state.parcelPanel.polygons[state.parcelPanel.currentPolygonId];
    return {
        landUseValues: currentPolygon && currentPolygon.value && currentPolygon.landUseValues
    };
};

const mapDispatchToProps = (dispatch) => ({

});

export default compose(
    connect(mapStateToProps, mapDispatchToProps),
    withStyles(style)
)(LandUse);